import React, {useContext} from 'react'

import { CompanyData, EndUser } from '@driftt/api-types'
import { Button, Flex, Header, Link, styled, Text, UserAvatar } from '@driftt/dds'
import { faLinkedinIn } from '@driftt/dds.icons/brands/faLinkedinIn'
import { faSalesforce } from '@driftt/dds.icons/brands/faSalesforce'
import { faTwitter } from '@driftt/dds.icons/brands/faTwitter'

import { AccountDrawerContext } from 'context/accountDrawerContext'
import SocialLinkIconButtons from 'pages/CCRMAccountDetailsPage/AccountDetailsHeader/SocialLinkIconButton'
import { visitorUrl } from 'utils/paths'
import { segmentTrack, TrackType } from 'utils/tracking'

import liftAiLogo from '../../../assets/images/lift-ai-logo.png'

import { PersonalIntel } from '.'

const LINKEDIN_URL = 'https://www.linkedin.com/'
const TWITTER_URL = 'https://www.twitter.com/'
const GMAIL_BASE_URL = 'https://mail.google.com/mail/?view=cm&fs=1&to='

const getEndUserName = (user: EndUser): string =>
  user?.name || user?.email || 'Anonymous Site Visitor'

const LiftAIImageLogo = styled('img', {
  width: '70px'
})

interface CompanyInfoRowProps {
  contact: EndUser
  personalIntel: PersonalIntel | null
  companyIntel: CompanyData | null
  accountId: string
  salesforceData: any
  contactProperties: any
}

const ContactInfoRow = ({
  contact,
  contactProperties,
  personalIntel,
  companyIntel,
  accountId,
  salesforceData,
}: CompanyInfoRowProps) => {
  const { setIsOpen: setIsDrawerOpen, setAccountId: setSelectedAccountId } = useContext(AccountDrawerContext)
  
  const hasLiftAI = contactProperties.propertyDefinitions?.filter((pd: any) => pd.name === 'lift_ai_score')
  if (!contact) {
    return null
  }

  const title = personalIntel?.employmentTitle || contact?.attributes?.employment_title
  const companyName =
    personalIntel?.employmentName || contact?.attributes?.employment_name || companyIntel?.name

  const handleChatMessage = () => {
    segmentTrack(TrackType.CONTACT_DETAILS_CHAT_MESSAGE)
    window.open(visitorUrl(contact?.id))
  }

  const handleSendEmail = () => {
    if (contact?.email) {
      segmentTrack(TrackType.CONTACT_DETAILS_SEND_EMAIL)
      const mailToLink = `${GMAIL_BASE_URL}${contact?.email}`
      window.open(mailToLink)
    }
  }

  const salesforceAccount = salesforceData?.accounts ? salesforceData?.accounts[0] : {}

  const { attributes: { lift_ai_score = '-', lift_ai_intent_segment = '-' } = {} } =
    contact || ({} as any)

  return (
    <Flex
      fullWidth
      alignItems="center"
      justifyContent="space-between"
      css={{ borderBottom: '1px solid $gray3', paddingBottom: '$2' }}>
      <Flex alignItems="center" justifyContent="center" gap={2}>
        <UserAvatar
          css={{
            '& img': {
              objectFit: 'contain'
            }
          }}
          imageUrl={contact?.avatarUrl}
          name={getEndUserName(contact)}
          size="large"
        />
        <Flex direction="column" gap={0}>
          <Header variant="h1">{getEndUserName(contact)}</Header>
          {title && (
            <Flex gap={1}>
              <Text variant="body">{`${title} at`}</Text>
                <Link onClick={() => {
                  setIsDrawerOpen(true)
                  setSelectedAccountId(accountId)
                }}>
                  {companyName}
                </Link> 
            </Flex>
          )}
        </Flex>
      </Flex>
      {hasLiftAI && (
        <Flex alignItems="baseline">
          <Flex direction="column" gap="half">
            <Flex alignItems="baseline" gap="half">
              <Header variant="h4">Score:</Header>
              <Text variant="bodyBold"> {lift_ai_score}</Text>
            </Flex>
            <Flex alignItems="baseline" gap="half">
              <Header variant="h4">Intent:</Header>
              <Text
                variant="bodyBold"
                css={{
                  textTransform: 'uppercase'
                }}>
                {lift_ai_intent_segment}
              </Text>
            </Flex>
          </Flex>

          <Flex alignItems="center" direction="column" gap={0}>
            <Header
              css={{
                fontSize: '8px',
                textAlign: 'center',
                lineHeight: '1.2'
              }}
              variant="h4">
              Powered by
            </Header>
            <LiftAIImageLogo src={liftAiLogo} />
          </Flex>
        </Flex>
      )}

      <Flex>
        <SocialLinkIconButtons
          validation={salesforceData?.baseUrl && salesforceAccount}
          trackType={TrackType.ACCOUNT_DETAILS_HEADER_NAVIGATES_TO_SALESFORCE}
          href={`${salesforceData?.baseUrl}lightning/r/Account/${salesforceAccount?.Id}/view`}
          tooltip="Salesforce"
          icon={faSalesforce}
        />
        <SocialLinkIconButtons
          validation={!!personalIntel?.linkedinHandle}
          trackType={TrackType.ACCOUNT_DETAILS_HEADER_NAVIGATES_TO_LINKEDIN}
          href={`${LINKEDIN_URL}${personalIntel?.linkedinHandle?.toLocaleLowerCase()}`}
          tooltip="LinkedIn"
          icon={faLinkedinIn}
        />
        <SocialLinkIconButtons
          validation={!!personalIntel?.twitterHandle}
          trackType={TrackType.ACCOUNT_DETAILS_HEADER_NAVIGATES_TWITTER}
          href={`${TWITTER_URL}${personalIntel?.twitterHandle}`}
          tooltip="Twitter"
          icon={faTwitter}
        />
        {contact?.email && (
          <Button variant="secondary" onClick={handleSendEmail}>
            Send email
          </Button>
        )}

        <Button variant="primary" onClick={handleChatMessage}>
          Send chat message
        </Button>
      </Flex>
    </Flex>
  )
}

export default ContactInfoRow
